/**
 * Atlas Forms Client
 * Unified client integrating all form modules
 */

import type {
  FormSchema,
  FormDefinition,
  IControlRegistry,
  IThemeResolver,
  StorageAdapter,
  ApiResponse,
  ListFormsRequest,
  ListFormsResponse,
} from '@atlas-forms/types-js';

import { getGlobalRegistry as getControlRegistry } from '@atlas-forms/control-registry-js';
import { getGlobalThemeResolver } from '@atlas-forms/themes-js';
import { LocalStorageAdapter } from '@atlas-forms/storage-js';
import { FormEngine } from '@atlas-forms/form-engine-js';
import { FormDefinitionApiClient, FormDataApiClient, updateAtlasApiConfig } from '@atlas-forms/api-client-js';
import { validateSchema } from '@atlas-forms/schema-js';
import { SchemaInheritanceResolver } from './schema-inheritance-resolver';
import { serializeFormData } from './utils/file-serializer';

/**
 * Client configuration options
 */
export interface AtlasFormsClientConfig {
  apiBaseUrl?: string;
  apiTimeout?: number;
  storageAdapter?: StorageAdapter;
  controlRegistry?: IControlRegistry;
  themeResolver?: IThemeResolver;
  autoValidate?: boolean;
  enableLogging?: boolean;
  /** Tenant ID injected into every request as the X-Tenant-ID header. */
  tenantId?: number;
  /** Bearer token injected into every request's Authorization header — the per-session SSO
   *  token, refreshed by re-calling AtlasFormsClient.initialize() (not getInstance(), which
   *  reuses the existing singleton) whenever it rotates. */
  token?: string;
}

/**
 * Unified Atlas Forms Client
 * Central entry point for form management
 */
export class AtlasFormsClient {
  private static instance: AtlasFormsClient | null = null;

  // tenantId/token are deliberately excluded from Required<>: they're pass-through values
  // forwarded straight to updateAtlasApiConfig() in the constructor below, never stored on
  // this.config (see that call site's own comment for why they must stay optional-and-omit
  // rather than defaulted).
  private config: Required<Omit<AtlasFormsClientConfig, 'tenantId' | 'token'>>;
  private controlRegistry: IControlRegistry;
  private themeResolver: IThemeResolver;
  private storageAdapter: StorageAdapter;
  private formDefinitionClient: FormDefinitionApiClient;
  private formDataClient: FormDataApiClient;
  private inheritanceResolver: SchemaInheritanceResolver;

  /**
   * Private constructor - use getInstance()
   */
  private constructor(config: AtlasFormsClientConfig = {}) {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || '/',
      apiTimeout: config.apiTimeout || 30000,
      storageAdapter: config.storageAdapter || new LocalStorageAdapter(),
      controlRegistry: config.controlRegistry || getControlRegistry(),
      themeResolver: config.themeResolver || getGlobalThemeResolver(),
      autoValidate: config.autoValidate !== false,
      enableLogging: config.enableLogging !== false,
    };

    this.controlRegistry = this.config.controlRegistry;
    this.themeResolver = this.config.themeResolver;
    this.storageAdapter = this.config.storageAdapter;

    // Initialize API clients — update global atlasApiConfig so all singletons pick it up.
    // tenantId/token were previously accepted by this class's own config interface (every
    // caller across the workspace already passed them — flow-studio's App.tsx, this app's
    // AppShell.tsx) but silently dropped here: neither field was ever forwarded to
    // atlasApiConfig, so every Atlas Forms API call went out unauthenticated (no
    // Authorization header, whatever X-Tenant-ID atlasApiConfig's own window.APP_CONFIG
    // fallback happened to have) regardless of what a caller passed in. Real, live-reproduced
    // bug: FormLookupField's search request came back 401 with a valid session token in hand.
    // Only forwarded when actually provided — omitting them here must NOT reset an
    // already-set atlasApiConfig.tenantId/token back to undefined on a later re-init that
    // only cares about e.g. apiBaseUrl.
    updateAtlasApiConfig({
      baseUrl: this.config.apiBaseUrl,
      timeout: this.config.apiTimeout,
      ...(config.tenantId !== undefined ? { tenantId: config.tenantId } : {}),
      ...(config.token !== undefined ? { token: config.token } : {}),
    });

    this.formDefinitionClient = new FormDefinitionApiClient();
    this.formDataClient = new FormDataApiClient();

    // Initialize inheritance resolver with callback to load parent forms
    this.inheritanceResolver = new SchemaInheritanceResolver({
      maxDepth: 5,
      onLoadParentForm: async (formID: number) => this.loadFormDefinitionInternal(formID),
    });

    this.log('Client initialized', { baseUrl: this.config.apiBaseUrl });
  }

  /**
   * Get or create global client instance
   */
  static getInstance(config?: AtlasFormsClientConfig): AtlasFormsClient {
    if (!AtlasFormsClient.instance) {
      AtlasFormsClient.instance = new AtlasFormsClient(config);
    }
    return AtlasFormsClient.instance;
  }

  /**
   * Initialize client (static method)
   */
  static initialize(config: AtlasFormsClientConfig): AtlasFormsClient {
    AtlasFormsClient.instance = new AtlasFormsClient(config);
    return AtlasFormsClient.instance;
  }

  /**
   * Get control registry
   */
  getControlRegistry(): IControlRegistry {
    return this.controlRegistry;
  }

  /**
   * Get theme resolver
   */
  getThemeResolver(): IThemeResolver {
    return this.themeResolver;
  }

  /**
   * Get storage adapter
   */
  getStorageAdapter(): StorageAdapter {
    return this.storageAdapter;
  }

  /**
   * Create form engine from schema
   */
  createFormEngine(schema: FormSchema, initialValues?: Record<string, unknown>): FormEngine {
    // Validate schema
    if (this.config.autoValidate) {
      const validation = validateSchema(schema);
      if (!validation.isValid) {
        this.log('Schema validation failed', validation.errors);
        throw new Error(`Invalid form schema: ${Object.values(validation.errors).join(', ')}`);
      }
    }

    const engine = new FormEngine(schema, initialValues);
    this.log('Form engine created');
    return engine;
  }

  /**
   * Get the raw FormDefinition record by ID (metadata only, no schema parsing).
   * Returns undefined if not found instead of throwing.
   */
  async getFormDefinition(formID: number): Promise<FormDefinition | undefined> {
    try {
      this.log('Getting form definition', { formID });
      const response = await this.formDefinitionClient.getFormById(formID);
      if (!response.success || !response.data) return undefined;
      const raw = response.data as any;
      return {
        ...raw,
        FormID: raw.FormID ?? raw.FormId ?? formID,
        Name: raw.Name ?? raw.name ?? String(formID),
        Title: raw.Title != null ? raw.Title : (raw.title != null ? raw.title : undefined),
        Enabled: raw.Enabled ?? raw.enabled ?? true,
        DisplayOrder: raw.DisplayOrder ?? raw.displayOrder ?? 0,
      } as FormDefinition;
    } catch {
      return undefined;
    }
  }

  /**
   * Update the raw FormDefinition record by ID (e.g. persisting a re-saved Schema from the
   * designer). Mirrors getFormDefinition's request/response shape; throws on failure so
   * callers can surface the error the same way loadFormDefinition's callers already do.
   */
  async updateFormDefinition(formID: number, updates: Partial<FormDefinition>): Promise<FormDefinition> {
    try {
      this.log('Updating form definition', { formID });
      const response = await this.formDefinitionClient.updateForm(formID, updates as Record<string, unknown>);
      if (!response.success || !response.data) {
        throw new Error('Failed to update form');
      }
      return response.data;
    } catch (error) {
      this.error('Failed to update form definition', error);
      throw error;
    }
  }

  /**
   * Load form definition by ID
   */
  async loadFormDefinition(formID: number): Promise<FormSchema> {
    try {
      this.log('Loading form definition', { formID });
      const schema = await this.loadFormDefinitionInternal(formID);

      // Resolve control inheritance (merge parent control properties into child controls)
      const resolvedSchema = await this.inheritanceResolver.resolveSchema(schema, formID);

      this.log('Form definition loaded and inheritance resolved', { formID });
      return resolvedSchema;
    } catch (error) {
      this.error('Failed to load form definition', error);
      throw error;
    }
  }

  /**
   * Internal: Load raw form schema without resolving inheritance
   */
  private async loadFormDefinitionInternal(formID: number): Promise<FormSchema> {
    try {
      const response = await this.formDefinitionClient.getFormById(formID);
      if (!response.success || !response.data) {
        throw new Error('Form not found');
      }
      const form = response.data as any;
      const schemaStr = form?.Schema ?? form?.schema;
      if (!schemaStr) throw new Error('Form has no schema');
      return typeof schemaStr === 'string' ? JSON.parse(schemaStr) : schemaStr;
    } catch (error) {
      this.error('Failed to load form definition internally', error);
      throw error;
    }
  }

  /**
   * Load form definition by name
   */
  async loadFormDefinitionByName(name: string): Promise<FormSchema> {
    try {
      this.log('Loading form by name', { name });
      const response = await this.formDefinitionClient.getFormByName(name);
      if (!response.success || !response.data) {
        throw new Error('Form not found');
      }
      const form = response.data as any;
      const schemaStr = form?.Schema ?? form?.schema;
      if (!schemaStr) throw new Error('Form has no schema');
      const schema = typeof schemaStr === 'string' ? JSON.parse(schemaStr) : schemaStr;

      // Resolve control inheritance
      const resolvedSchema = await this.inheritanceResolver.resolveSchema(schema);

      this.log('Form by name loaded and inheritance resolved', { name });
      return resolvedSchema;
    } catch (error) {
      this.error('Failed to load form by name', error);
      throw error;
    }
  }

  /**
   * List available forms
   */
  async listForms(request?: ListFormsRequest): Promise<ApiResponse<ListFormsResponse>> {
    try {
      this.log('Listing forms', request?.search);
      const apiRequest = {
        PageInfo: {
          PageNumber: request?.page ?? 1,
          PageSize: request?.pageSize ?? 10,
        },
        ...(request?.search ? { SearchTitle: request.search } : {}),
        ...(request?.categoryId !== undefined ? { CategoryId: request.categoryId } : {}),
        ...(request?.enabled !== undefined ? { IsEnabled: request.enabled } : {}),
        ...(request?.isNodeForm !== undefined ? { IsNodeForm: request.isNodeForm } : {}),
      };
      const response = await this.formDefinitionClient.listForms(apiRequest);
      if (!response.success || !response.data) return response as any;
      const raw = response.data as any;
      const rawItems: any[] = raw.Data ?? raw.items ?? [];
      const mapped: ListFormsResponse = {
        items: rawItems.map((item: any) => ({
          ...item,
          // Normalize: API may return formId (toPascalCase→FormId) or formID (→FormID)
          FormID: item.FormID ?? item.FormId ?? item.Id ?? item.id,
          Name: item.Name ?? item.name ?? '',
          // null-to-undefined so downstream ?? chains work correctly
          Title: item.Title != null ? item.Title : (item.title != null ? item.title : undefined),
        })),
        page: raw.PageInfo?.PageNumber ?? 1,
        pageSize: raw.PageInfo?.PageSize ?? 10,
        totalCount: raw.PageInfo?.TotalRecords ?? 0,
        totalPages: raw.PageInfo?.TotalPages ?? 0,
        hasNextPage: (raw.PageInfo?.PageNumber ?? 1) < (raw.PageInfo?.TotalPages ?? 1),
        hasPreviousPage: (raw.PageInfo?.PageNumber ?? 1) > 1,
      };
      return { statusCode: 200, success: true, data: mapped };
    } catch (error) {
      this.error('Failed to list forms', error);
      throw error;
    }
  }

  /**
   * Submit form data
   *
   * Serializes File objects to base64 before submission to ensure
   * files are properly stored in the database.
   */
  async submitForm(
    formID: number,
    data: Record<string, unknown>,
    metadata?: Record<string, unknown>
  ): Promise<ApiResponse<unknown>> {
    try {
      this.log('Submitting form', { formID });

      // ✅ Serialize files before submission
      const serializationResult = await serializeFormData(data);

      if (!serializationResult.success) {
        this.error('File serialization errors', {
          errors: serializationResult.errors,
        });
        // Fail submission if files present and serialization failed
        if (serializationResult.fileCount > 0) {
          throw new Error(
            `Failed to serialize files: ${serializationResult.errors
              .map((e) => e.error)
              .join(', ')}`
          );
        }
      }

      // Log file submission details
      if (serializationResult.fileCount > 0) {
        this.log('Form contains files', {
          fileCount: serializationResult.fileCount,
          totalSize: `${(serializationResult.totalSize / 1024 / 1024).toFixed(2)} MB`,
        });
      }

      // Submit with serialized data
      const response = await this.formDataClient.submitFormData({
        FormID: formID,
        FormData: JSON.stringify(serializationResult.data), // ✅ Use serialized data
        FormVersion: metadata?.version as string | undefined,
      });

      if (response.success) {
        this.log('Form submitted successfully', {
          recordId: response.data?.recordId,
          filesIncluded: serializationResult.fileCount > 0,
        });
      }

      return response;
    } catch (error) {
      this.error('Form submission failed', error);
      throw error;
    }
  }

  /**
   * Load form record by ID
   */
  async loadFormRecord(recordId: number) {
    try {
      this.log('Loading form record', { recordId });
      const response = await this.formDataClient.getFormRecord(recordId);
      return response;
    } catch (error) {
      this.error('Failed to load form record', error);
      throw error;
    }
  }

  /**
   * Save form draft
   */
  async saveDraft(formID: string | number, values: Record<string, unknown>): Promise<void> {
    try {
      this.log('Saving form draft', { formID });
      await this.storageAdapter.saveDraft(String(formID), values);
      this.log('Draft saved');
    } catch (error) {
      this.error('Failed to save draft', error);
      throw error;
    }
  }

  /**
   * Load form draft
   */
  async loadDraft(formID: string | number): Promise<Record<string, unknown> | null> {
    try {
      this.log('Loading form draft', { formID });
      const draft = await this.storageAdapter.loadDraft(String(formID));
      if (draft) {
        this.log('Draft loaded');
      }
      return draft;
    } catch (error) {
      this.error('Failed to load draft', error);
      throw error;
    }
  }

  /**
   * Clear form draft
   */
  async clearDraft(formID: string | number): Promise<void> {
    try {
      this.log('Clearing form draft', { formID });
      await this.storageAdapter.clearDraft(String(formID));
      this.log('Draft cleared');
    } catch (error) {
      this.error('Failed to clear draft', error);
      throw error;
    }
  }

  /**
   * Apply and persist inheritance in schema
   * Merges inherited control properties and removes inheritance references
   * This permanently bakes the inheritance into the schema for storage
   */
  async applyAndPersistInheritance(schema: FormSchema, currentFormID?: number): Promise<FormSchema> {
    try {
      this.log('Applying and persisting inheritance', { formID: currentFormID });
      const resolvedSchema = await this.inheritanceResolver.resolveSchema(schema, currentFormID);

      // Now remove the inheritance references since they're baked into the control
      const persistedControls = (resolvedSchema.controls || []).map(control => {
        const { referredFormID, referredControlID, controlInheritType, ...persistedControl } = control as any;
        // Keep the control as-is now that properties are merged
        return persistedControl;
      });

      const persistedSchema = {
        ...resolvedSchema,
        controls: persistedControls,
      };

      this.log('Inheritance applied and persisted', { formID: currentFormID });
      return persistedSchema;
    } catch (error) {
      this.error('Failed to apply and persist inheritance', error);
      throw error;
    }
  }

  /**
   * Get client configuration
   */
  getConfig(): Readonly<AtlasFormsClientConfig> {
    return { ...this.config };
  }

  /**
   * Internal logging
   */
  private log(message: string, data?: any): void {
    if (this.config.enableLogging) {
      console.log(`[AtlasFormsClient] ${message}`, data);
    }
  }

  /**
   * Internal error logging
   */
  private error(message: string, error?: any): void {
    if (this.config.enableLogging) {
      console.error(`[AtlasFormsClient] ${message}`, error);
    }
  }

  /**
   * Reset client (for testing)
   */
  static reset(): void {
    AtlasFormsClient.instance = null;
  }
}
